import React, { useCallback, useState, useEffect } from 'react';
import ReactFlow, {
  addEdge,
  MiniMap,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  useReactFlow,
  Node,
  Edge,
  OnConnect,
  Connection,
  NodeChange,
  EdgeChange,
  applyNodeChanges,
  applyEdgeChanges,
  Handle,
  Position,
  NodeProps,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Play, Square, GitBranch, Bot, Workflow, X, Save, Download, MessageSquare, MessageCircle, Plus, Database, Globe, Shield, FileText, TestTube, Send, Trash2, RefreshCw } from 'lucide-react';

// Node data interface
interface ConversationNodeData {
  label: string;
  type: string;
  icon: React.ReactNode;
  onOpenModal: (id: string) => void;
  settings?: Record<string, any>;
}

// Mock data for agents and workflows
const mockAgents = [
  {
    id: 'bank-statement',
    name: 'Bank Statement Verification',
    description: 'Verifies user identity for bank statement access',
    parameters: {
      required: ['$role', '$tenantId', 'dob', 'pin', 'idNumber'],
      optional: ['$uid', '$sessionId', 'accountType'],
      outputs: ['verification_status', 'access_token', 'statement_url']
    },
    userMessages: {
      dob: 'Please share your date of birth (DD/MM/YYYY)',
      pin: 'Please enter your 4-digit PIN',
      idNumber: 'Please provide your government ID number'
    }
  },
  {
    id: 'loan-application',
    name: 'Loan Application Agent',
    description: 'Handles loan application process and validation',
    parameters: {
      required: ['$role', '$tenantId', 'income', 'employment', 'loanAmount'],
      optional: ['$uid', 'creditScore', 'collateral'],
      outputs: ['application_id', 'eligibility_status', 'next_steps']
    },
    userMessages: {
      income: 'What is your monthly income?',
      employment: 'Please provide your employment details',
      loanAmount: 'How much loan amount do you need?'
    }
  },
  {
    id: 'customer-support',
    name: 'Customer Support Agent',
    description: 'Handles customer inquiries and support tickets',
    parameters: {
      required: ['$role', '$tenantId', 'issueType', 'priority'],
      optional: ['$uid', '$sessionId', 'previousTickets'],
      outputs: ['ticket_id', 'resolution_status', 'escalation_needed']
    },
    userMessages: {
      issueType: 'Please describe your issue',
      priority: 'How urgent is this issue? (Low/Medium/High)'
    }
  }
];

const mockWorkflows = [
  {
    id: 'kyc-verification',
    name: 'KYC Verification Flow',
    description: 'Complete Know Your Customer verification process',
    parameters: {
      required: ['$tenantId', 'documentType', 'documentNumber'],
      optional: ['$uid', 'nationality', 'address'],
      outputs: ['kyc_status', 'verification_id', 'compliance_score']
    },
    userMessages: {
      documentType: 'What type of document do you want to verify? (Passport/License/ID)',
      documentNumber: 'Please enter your document number'
    }
  },
  {
    id: 'payment-processing',
    name: 'Payment Processing',
    description: 'Handle payment transactions and billing',
    parameters: {
      required: ['$tenantId', 'amount', 'currency'],
      optional: ['$uid', 'paymentMethod', 'billingAddress'],
      outputs: ['transaction_id', 'payment_status', 'receipt_url']
    },
    userMessages: {
      amount: 'Enter the payment amount',
      currency: 'Select currency (USD/EUR/GBP)'
    }
  }
];

// Node types for the sidebar
const nodeTypes = [
  { type: 'start', icon: <Play className="w-3 h-3 text-green-400" />, label: 'Start' },
  { type: 'message', icon: <MessageSquare className="w-3 h-3 text-blue-400" />, label: 'Message' },
  { type: 'response', icon: <MessageCircle className="w-3 h-3 text-indigo-400" />, label: 'Response' },
  { type: 'agent', icon: <Bot className="w-3 h-3 text-purple-400" />, label: 'Agent' },
  { type: 'workflow', icon: <Workflow className="w-3 h-3 text-blue-400" />, label: 'Workflow' },
  { type: 'condition', icon: <GitBranch className="w-3 h-3 text-yellow-400" />, label: 'Condition' },
  { type: 'api', icon: <Globe className="w-3 h-3 text-cyan-400" />, label: 'API Call' },
  { type: 'database', icon: <Database className="w-3 h-3 text-orange-400" />, label: 'Database' },
  { type: 'validation', icon: <Shield className="w-3 h-3 text-emerald-400" />, label: 'Validation' },
  { type: 'document', icon: <FileText className="w-3 h-3 text-pink-400" />, label: 'Document' },
  { type: 'end', icon: <Square className="w-3 h-3 text-red-400" />, label: 'End' },
];

// Default template data
const createDefaultTemplate = () => {
  const templateNodes: Node<ConversationNodeData>[] = [
    {
      id: 'start-1',
      type: 'conversationNode',
      position: { x: 50, y: 200 },
      data: {
        label: 'Start',
        type: 'start',
        icon: <Play className="w-3 h-3 text-green-400" />,
        onOpenModal: () => {},
        settings: { description: 'Loan application process start' }
      }
    },
    {
      id: 'message-1',
      type: 'conversationNode',
      position: { x: 200, y: 200 },
      data: {
        label: 'Greeting',
        type: 'message',
        icon: <MessageSquare className="w-3 h-3 text-blue-400" />,
        onOpenModal: () => {},
        settings: { 
          expectedIntent: 'loan_application',
          intentLabel: 'Loan Request',
          expectedValues: 'loan_type, amount, purpose'
        }
      }
    },
    {
      id: 'response-1',
      type: 'conversationNode',
      position: { x: 380, y: 200 },
      data: {
        label: 'Welcome',
        type: 'response',
        icon: <MessageCircle className="w-3 h-3 text-indigo-400" />,
        onOpenModal: () => {},
        settings: { 
          messages: [
            { text: 'Welcome to ABC Bank! I\'ll help you with your loan application.', type: 'text' },
            { text: 'To get started, I need to verify your identity and gather some information.', type: 'text' }
          ]
        }
      }
    },
    {
      id: 'agent-1',
      type: 'conversationNode',
      position: { x: 560, y: 200 },
      data: {
        label: 'KYC Agent',
        type: 'agent',
        icon: <Bot className="w-3 h-3 text-purple-400" />,
        onOpenModal: () => {},
        settings: {
          selectedAgent: 'bank-statement',
          parameterMessages: {
            dob: 'Please provide your date of birth (DD/MM/YYYY)',
            pin: 'Enter your 4-digit security PIN',
            idNumber: 'Please share your government ID number'
          }
        }
      }
    },
    {
      id: 'validation-1',
      type: 'conversationNode',
      position: { x: 740, y: 200 },
      data: {
        label: 'ID Validation',
        type: 'validation',
        icon: <Shield className="w-3 h-3 text-emerald-400" />,
        onOpenModal: () => {},
        settings: {
          validationType: 'custom',
          regex: '^[A-Z0-9]{8,12}$'
        }
      }
    },
    {
      id: 'condition-1',
      type: 'conversationNode',
      position: { x: 920, y: 200 },
      data: {
        label: 'KYC Valid?',
        type: 'condition',
        icon: <GitBranch className="w-3 h-3 text-yellow-400" />,
        onOpenModal: () => {},
        settings: {
          condition: 'kyc_status == "verified"',
          trueBranch: 'Verified',
          falseBranch: 'Failed'
        }
      }
    },
    {
      id: 'agent-2',
      type: 'conversationNode',
      position: { x: 1100, y: 120 },
      data: {
        label: 'Loan Agent',
        type: 'agent',
        icon: <Bot className="w-3 h-3 text-purple-400" />,
        onOpenModal: () => {},
        settings: {
          selectedAgent: 'loan-application',
          parameterMessages: {
            income: 'What is your monthly income? (Please provide in USD)',
            employment: 'Please describe your current employment (Company, Position, Years)',
            loanAmount: 'How much loan amount do you need? (Minimum $5,000)'
          }
        }
      }
    },
    {
      id: 'api-1',
      type: 'conversationNode',
      position: { x: 1280, y: 120 },
      data: {
        label: 'Credit Check',
        type: 'api',
        icon: <Globe className="w-3 h-3 text-cyan-400" />,
        onOpenModal: () => {},
        settings: {
          endpoint: 'https://api.creditbureau.com/score',
          method: 'POST',
          timeout: 10000
        }
      }
    },
    {
      id: 'database-1',
      type: 'conversationNode',
      position: { x: 1460, y: 120 },
      data: {
        label: 'Save Application',
        type: 'database',
        icon: <Database className="w-3 h-3 text-orange-400" />,
        onOpenModal: () => {},
        settings: {
          queryType: 'INSERT',
          tableName: 'loan_applications'
        }
      }
    },
    {
      id: 'condition-2',
      type: 'conversationNode',
      position: { x: 1640, y: 120 },
      data: {
        label: 'Credit Score?',
        type: 'condition',
        icon: <GitBranch className="w-3 h-3 text-yellow-400" />,
        onOpenModal: () => {},
        settings: {
          condition: 'credit_score >= 650',
          trueBranch: 'Good',
          falseBranch: 'Poor'
        }
      }
    },
    {
      id: 'workflow-1',
      type: 'conversationNode',
      position: { x: 1820, y: 60 },
      data: {
        label: 'Approval Flow',
        type: 'workflow',
        icon: <Workflow className="w-3 h-3 text-blue-400" />,
        onOpenModal: () => {},
        settings: {
          selectedWorkflow: 'kyc-verification',
          parameterMessages: {
            documentType: 'Please upload your income proof document (Salary slip/Bank statement)',
            documentNumber: 'Enter the document reference number'
          }
        }
      }
    },
    {
      id: 'response-2',
      type: 'conversationNode',
      position: { x: 2000, y: 60 },
      data: {
        label: 'Approved',
        type: 'response',
        icon: <MessageCircle className="w-3 h-3 text-indigo-400" />,
        onOpenModal: () => {},
        settings: {
          messages: [
            { text: '🎉 Congratulations! Your loan application has been approved.', type: 'text' },
            { text: 'You will receive the loan agreement within 24 hours.', type: 'text' },
            { text: 'Thank you for choosing ABC Bank!', type: 'text' }
          ]
        }
      }
    },
    {
      id: 'response-3',
      type: 'conversationNode',
      position: { x: 1820, y: 180 },
      data: {
        label: 'Manual Review',
        type: 'response',
        icon: <MessageCircle className="w-3 h-3 text-indigo-400" />,
        onOpenModal: () => {},
        settings: {
          messages: [
            { text: 'Your application requires manual review due to credit score.', type: 'text' },
            { text: 'Our team will contact you within 2-3 business days.', type: 'text' }
          ]
        }
      }
    },
    {
      id: 'response-4',
      type: 'conversationNode',
      position: { x: 1100, y: 280 },
      data: {
        label: 'KYC Failed',
        type: 'response',
        icon: <MessageCircle className="w-3 h-3 text-indigo-400" />,
        onOpenModal: () => {},
        settings: {
          messages: [
            { text: 'Sorry, we couldn\'t verify your identity with the provided information.', type: 'text' },
            { text: 'Please visit our nearest branch with original documents.', type: 'text' }
          ]
        }
      }
    },
    {
      id: 'end-1',
      type: 'conversationNode',
      position: { x: 2180, y: 60 },
      data: {
        label: 'Success',
        type: 'end',
        icon: <Square className="w-3 h-3 text-red-400" />,
        onOpenModal: () => {},
        settings: { description: 'Loan approved successfully' }
      }
    },
    {
      id: 'end-2',
      type: 'conversationNode',
      position: { x: 2000, y: 180 },
      data: {
        label: 'Pending',
        type: 'end',
        icon: <Square className="w-3 h-3 text-red-400" />,
        onOpenModal: () => {},
        settings: { description: 'Manual review pending' }
      }
    },
    {
      id: 'end-3',
      type: 'conversationNode',
      position: { x: 1280, y: 280 },
      data: {
        label: 'Failed',
        type: 'end',
        icon: <Square className="w-3 h-3 text-red-400" />,
        onOpenModal: () => {},
        settings: { description: 'KYC verification failed' }
      }
    }
  ];

  const templateEdges: Edge[] = [
    { id: 'e1', source: 'start-1', target: 'message-1', style: { strokeWidth: 2, stroke: '#6b7280' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#6b7280', width: 15, height: 15 } },
    { id: 'e2', source: 'message-1', target: 'response-1', style: { strokeWidth: 2, stroke: '#6b7280' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#6b7280', width: 15, height: 15 } },
    { id: 'e3', source: 'response-1', target: 'agent-1', style: { strokeWidth: 2, stroke: '#6b7280' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#6b7280', width: 15, height: 15 } },
    { id: 'e4', source: 'agent-1', target: 'validation-1', style: { strokeWidth: 2, stroke: '#6b7280' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#6b7280', width: 15, height: 15 } },
    { id: 'e5', source: 'validation-1', target: 'condition-1', style: { strokeWidth: 2, stroke: '#6b7280' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#6b7280', width: 15, height: 15 } },
    { id: 'e6', source: 'condition-1', sourceHandle: 'true', target: 'agent-2', style: { strokeWidth: 2, stroke: '#22c55e' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#22c55e', width: 15, height: 15 } },
    { id: 'e7', source: 'condition-1', sourceHandle: 'false', target: 'response-4', style: { strokeWidth: 2, stroke: '#ef4444' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#ef4444', width: 15, height: 15 } },
    { id: 'e8', source: 'agent-2', target: 'api-1', style: { strokeWidth: 2, stroke: '#6b7280' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#6b7280', width: 15, height: 15 } },
    { id: 'e9', source: 'api-1', target: 'database-1', style: { strokeWidth: 2, stroke: '#6b7280' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#6b7280', width: 15, height: 15 } },
    { id: 'e10', source: 'database-1', target: 'condition-2', style: { strokeWidth: 2, stroke: '#6b7280' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#6b7280', width: 15, height: 15 } },
    { id: 'e11', source: 'condition-2', sourceHandle: 'true', target: 'workflow-1', style: { strokeWidth: 2, stroke: '#22c55e' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#22c55e', width: 15, height: 15 } },
    { id: 'e12', source: 'condition-2', sourceHandle: 'false', target: 'response-3', style: { strokeWidth: 2, stroke: '#ef4444' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#ef4444', width: 15, height: 15 } },
    { id: 'e13', source: 'workflow-1', target: 'response-2', style: { strokeWidth: 2, stroke: '#6b7280' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#6b7280', width: 15, height: 15 } },
    { id: 'e14', source: 'response-2', target: 'end-1', style: { strokeWidth: 2, stroke: '#6b7280' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#6b7280', width: 15, height: 15 } },
    { id: 'e15', source: 'response-3', target: 'end-2', style: { strokeWidth: 2, stroke: '#6b7280' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#6b7280', width: 15, height: 15 } },
    { id: 'e16', source: 'response-4', target: 'end-3', style: { strokeWidth: 2, stroke: '#6b7280' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#6b7280', width: 15, height: 15 } }
  ];

  return { templateNodes, templateEdges };
};

// Custom Node Component
const ConversationNode: React.FC<NodeProps<ConversationNodeData>> = ({ id, data, selected }) => {
  const getNodeColor = (type: string) => {
    switch (type) {
      case 'start': return 'border-green-400 bg-green-950/40';
      case 'end': return 'border-red-400 bg-red-950/40';
      case 'message': return 'border-blue-400 bg-blue-950/40';
      case 'response': return 'border-indigo-400 bg-indigo-950/40';
      case 'workflow': return 'border-blue-400 bg-blue-950/40';
      case 'agent': return 'border-purple-400 bg-purple-950/40';
      case 'condition': return 'border-yellow-400 bg-yellow-950/40';
      case 'api': return 'border-cyan-400 bg-cyan-950/40';
      case 'database': return 'border-orange-400 bg-orange-950/40';
      case 'validation': return 'border-emerald-400 bg-emerald-950/40';
      case 'document': return 'border-pink-400 bg-pink-950/40';
      default: return 'border-gray-400 bg-gray-950/40';
    }
  };

  const renderHandles = () => {
    if (data.type === 'condition') {
      return (
        <>
          <Handle
            type="target"
            position={Position.Left}
            className="w-2 h-2 bg-gray-500 border border-white/60"
          />
          <Handle
            type="source"
            position={Position.Right}
            id="true"
            style={{ top: '30%' }}
            className="w-2 h-2 bg-green-500 border border-white/60"
          />
          <Handle
            type="source"
            position={Position.Right}
            id="false"
            style={{ top: '70%' }}
            className="w-2 h-2 bg-red-500 border border-white/60"
          />
          {/* True/False labels */}
          <div className="absolute -right-8 top-2 text-xs text-green-400 font-medium">True</div>
          <div className="absolute -right-8 bottom-2 text-xs text-red-400 font-medium">False</div>
        </>
      );
    }

    return (
      <>
        {data.type !== 'start' && (
          <Handle
            type="target"
            position={Position.Left}
            className="w-2 h-2 bg-gray-500 border border-white/60"
          />
        )}
        {data.type !== 'end' && (
          <Handle
            type="source"
            position={Position.Right}
            className="w-2 h-2 bg-gray-500 border border-white/60"
          />
        )}
      </>
    );
  };

  return (
    <div
      className={`px-2 py-1.5 shadow-lg rounded border bg-gray-900/95 text-white min-w-20 cursor-pointer transition-all hover:shadow-xl relative ${getNodeColor(data.type)} ${
        selected ? 'ring-1 ring-white/60 shadow-white/20' : ''
      }`}
      onClick={(e) => {
        e.stopPropagation();
        data.onOpenModal(id);
      }}
    >
      {renderHandles()}
      
      <div className="flex flex-col items-center gap-0.5">
        {data.icon}
        <div className="text-xs font-medium text-center leading-tight max-w-16 truncate">
          {data.label}
        </div>
        {data.settings?.intentLabel && (
          <div className="text-xs text-gray-400 truncate max-w-16">
            {data.settings.intentLabel}
          </div>
        )}
      </div>
    </div>
  );
};

// Sidebar Component
const ConversationSidebar: React.FC = () => {
  const onDragStart = (event: React.DragEvent<HTMLDivElement>, nodeType: string) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div className="w-52 bg-gray-900/95 border-r border-gray-700/60 p-3">
      <h3 className="text-sm font-semibold text-white mb-3">Components</h3>
      <div className="space-y-1.5">
        {nodeTypes.map((node) => (
          <div
            key={node.type}
            className="flex items-center gap-2 p-2 bg-gray-800/60 rounded border border-gray-600/40 hover:bg-gray-700/60 cursor-grab transition-colors text-xs"
            onDragStart={(event) => onDragStart(event, node.type)}
            draggable
          >
            {node.icon}
            <span className="font-medium text-white">{node.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Test Chatbot Component
interface TestChatbotProps {
  onClose: () => void;
}

const TestChatbot: React.FC<TestChatbotProps> = ({ onClose }) => {
  const [messages, setMessages] = useState([
    { text: 'Hello! I\'m your test bot. How can I help you today?', sender: 'bot' }
  ]);
  const [input, setInput] = useState('');

  const sendMessage = () => {
    if (!input.trim()) return;
    
    const newMessages = [
      ...messages,
      { text: input, sender: 'user' },
      { text: 'This is a test response. In a real implementation, this would process through your conversation graph.', sender: 'bot' }
    ];
    
    setMessages(newMessages);
    setInput('');
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg border border-gray-600 shadow-2xl w-full max-w-md h-96 flex flex-col">
        <div className="flex items-center justify-between p-3 border-b border-gray-700">
          <h3 className="text-sm font-semibold text-white">Test Chatbot</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-xs px-3 py-2 rounded-lg text-xs ${
                  message.sender === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-200'
                }`}
              >
                {message.text}
              </div>
            </div>
          ))}
        </div>
        
        <div className="p-3 border-t border-gray-700">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Type your message..."
              className="flex-1 px-3 py-2 text-xs bg-gray-700 border border-gray-600 rounded text-white"
            />
            <button
              onClick={sendMessage}
              className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded transition-colors"
            >
              <Send className="w-3 h-3 text-white" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Configuration Modal Component
interface ConfigModalProps {
  node: Node<ConversationNodeData> | null;
  onSave: (label: string, settings: Record<string, any>) => void;
  onDelete: () => void;
  onClose: () => void;
}

const ConfigModal: React.FC<ConfigModalProps> = ({ node, onSave, onDelete, onClose }) => {
  const [label, setLabel] = useState(node?.data.label || '');
  const [settings, setSettings] = useState<Record<string, any>>(node?.data.settings || {});

  if (!node) return null;

  const handleSave = () => {
    onSave(label, settings);
  };

  const addResponseMessage = () => {
    const messages = settings.messages || [];
    setSettings({
      ...settings,
      messages: [...messages, { text: '', type: 'text' }]
    });
  };

  const updateResponseMessage = (index: number, text: string) => {
    const messages = [...(settings.messages || [])];
    messages[index] = { ...messages[index], text };
    setSettings({ ...settings, messages });
  };

  const removeResponseMessage = (index: number) => {
    const messages = [...(settings.messages || [])];
    messages.splice(index, 1);
    setSettings({ ...settings, messages });
  };

  const updateParameterMessage = (paramName: string, message: string) => {
    const parameterMessages = { ...settings.parameterMessages };
    parameterMessages[paramName] = message;
    setSettings({ ...settings, parameterMessages });
  };

  const renderParameterMapping = (agent: any) => {
    const userParams = agent.parameters.required.filter((p: string) => !p.startsWith('$'));
    
    return (
      <div className="border-l border-gray-600 pl-3 ml-3">
        <h4 className="text-xs font-medium text-gray-300 mb-2">User Message Mapping</h4>
        <div className="space-y-2">
          {userParams.map((param: string) => (
            <div key={param}>
              <label className="block text-xs text-gray-400 mb-1 capitalize">
                {param.replace(/([A-Z])/g, ' $1').trim()}
              </label>
              <textarea
                className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white resize-none"
                rows={2}
                value={settings.parameterMessages?.[param] || agent.userMessages?.[param] || ''}
                onChange={(e) => updateParameterMessage(param, e.target.value)}
                placeholder={`Message to ask user for ${param}...`}
              />
              <div className="text-xs text-gray-500 mt-1">
                Users can provide multiple values separated by comma (,) or pipe (|)
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTypeSpecificSettings = () => {
    switch (node.data.type) {
      case 'message':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Expected Intent</label>
              <input
                type="text"
                className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
                value={settings.expectedIntent || ''}
                onChange={(e) => setSettings({ ...settings, expectedIntent: e.target.value })}
                placeholder="e.g., greeting, support_request, complaint"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Intent Label</label>
              <input
                type="text"
                className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
                value={settings.intentLabel || ''}
                onChange={(e) => setSettings({ ...settings, intentLabel: e.target.value })}
                placeholder="Label to show on node"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Expected Values</label>
              <textarea
                className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white resize-none"
                rows={3}
                value={settings.expectedValues || ''}
                onChange={(e) => setSettings({ ...settings, expectedValues: e.target.value })}
                placeholder="user_name, email, issue_type..."
              />
            </div>
          </div>
        );

      case 'response':
        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-medium text-gray-300">Response Messages</label>
              <button
                onClick={addResponseMessage}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 rounded transition-colors"
              >
                <Plus className="w-3 h-3" />
                Add
              </button>
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {(settings.messages || []).map((message: any, index: number) => (
                <div key={index} className="flex gap-2">
                  <textarea
                    className="flex-1 p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white resize-none"
                    rows={2}
                    value={message.text}
                    onChange={(e) => updateResponseMessage(index, e.target.value)}
                    placeholder={`Message ${index + 1}...`}
                  />
                  <button
                    onClick={() => removeResponseMessage(index)}
                    className="text-red-400 hover:text-red-300 p-1"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        );

      case 'agent':
        const selectedAgent = mockAgents.find(a => a.id === settings.selectedAgent);
        return (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">Select Agent</label>
                <select
                  className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
                  value={settings.selectedAgent || ''}
                  onChange={(e) => {
                    const agent = mockAgents.find(a => a.id === e.target.value);
                    setSettings({ 
                      ...settings, 
                      selectedAgent: e.target.value,
                      agentConfig: agent ? agent.parameters : {},
                      parameterMessages: agent ? { ...agent.userMessages } : {}
                    });
                  }}
                >
                  <option value="">Choose an agent...</option>
                  {mockAgents.map((agent) => (
                    <option key={agent.id} value={agent.id}>{agent.name}</option>
                  ))}
                </select>
              </div>
              
              {selectedAgent && (
                <div>
                  <div className="text-xs text-gray-400 mb-2">
                    {selectedAgent.description}
                  </div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Parameters</label>
                  <div className="bg-gray-700 p-2 rounded text-xs">
                    <pre className="text-gray-300 whitespace-pre-wrap">
                      {JSON.stringify(selectedAgent.parameters, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
            
            {selectedAgent && renderParameterMapping(selectedAgent)}
          </div>
        );

      case 'workflow':
        const selectedWorkflow = mockWorkflows.find(w => w.id === settings.selectedWorkflow);
        return (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">Select Workflow</label>
                <select
                  className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
                  value={settings.selectedWorkflow || ''}
                  onChange={(e) => {
                    const workflow = mockWorkflows.find(w => w.id === e.target.value);
                    setSettings({ 
                      ...settings, 
                      selectedWorkflow: e.target.value,
                      workflowConfig: workflow ? workflow.parameters : {},
                      parameterMessages: workflow ? { ...workflow.userMessages } : {}
                    });
                  }}
                >
                  <option value="">Choose a workflow...</option>
                  {mockWorkflows.map((workflow) => (
                    <option key={workflow.id} value={workflow.id}>{workflow.name}</option>
                  ))}
                </select>
              </div>
              
              {selectedWorkflow && (
                <div>
                  <div className="text-xs text-gray-400 mb-2">
                    {selectedWorkflow.description}
                  </div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Parameters</label>
                  <div className="bg-gray-700 p-2 rounded text-xs">
                    <pre className="text-gray-300 whitespace-pre-wrap">
                      {JSON.stringify(selectedWorkflow.parameters, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
            
            {selectedWorkflow && renderParameterMapping(selectedWorkflow)}
          </div>
        );

      case 'condition':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Condition</label>
              <input
                type="text"
                className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
                value={settings.condition || ''}
                onChange={(e) => setSettings({ ...settings, condition: e.target.value })}
                placeholder="e.g., user_intent == 'support'"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-green-400 mb-1">True Branch</label>
                <input
                  type="text"
                  className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
                  value={settings.trueBranch || 'Yes'}
                  onChange={(e) => setSettings({ ...settings, trueBranch: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-red-400 mb-1">False Branch</label>
                <input
                  type="text"
                  className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
                  value={settings.falseBranch || 'No'}
                  onChange={(e) => setSettings({ ...settings, falseBranch: e.target.value })}
                />
              </div>
            </div>
          </div>
        );

      case 'api':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">API Endpoint</label>
              <input
                type="text"
                className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
                value={settings.endpoint || ''}
                onChange={(e) => setSettings({ ...settings, endpoint: e.target.value })}
                placeholder="https://api.example.com/endpoint"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">Method</label>
                <select
                  className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
                  value={settings.method || 'GET'}
                  onChange={(e) => setSettings({ ...settings, method: e.target.value })}
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">Timeout (ms)</label>
                <input
                  type="number"
                  className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
                  value={settings.timeout || 5000}
                  onChange={(e) => setSettings({ ...settings, timeout: parseInt(e.target.value) })}
                />
              </div>
            </div>
          </div>
        );

      case 'database':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Query Type</label>
              <select
                className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
                value={settings.queryType || ''}
                onChange={(e) => setSettings({ ...settings, queryType: e.target.value })}
              >
                <option value="">Select query type...</option>
                <option value="SELECT">SELECT</option>
                <option value="INSERT">INSERT</option>
                <option value="UPDATE">UPDATE</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Table Name</label>
              <input
                type="text"
                className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
                value={settings.tableName || ''}
                onChange={(e) => setSettings({ ...settings, tableName: e.target.value })}
                placeholder="users, transactions, etc."
              />
            </div>
          </div>
        );

      case 'validation':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Validation Type</label>
              <select
                className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
                value={settings.validationType || ''}
                onChange={(e) => setSettings({ ...settings, validationType: e.target.value })}
              >
                <option value="">Select validation...</option>
                <option value="email">Email Format</option>
                <option value="phone">Phone Number</option>
                <option value="date">Date Format</option>
                <option value="custom">Custom Regex</option>
              </select>
            </div>
            {settings.validationType === 'custom' && (
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">Regex Pattern</label>
                <input
                  type="text"
                  className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
                  value={settings.regex || ''}
                  onChange={(e) => setSettings({ ...settings, regex: e.target.value })}
                  placeholder="^[A-Za-z0-9]+$"
                />
              </div>
            )}
          </div>
        );

      default:
        return (
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-1">Description</label>
            <textarea
              className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white resize-none"
              rows={3}
              value={settings.description || ''}
              onChange={(e) => setSettings({ ...settings, description: e.target.value })}
              placeholder="Enter description..."
            />
          </div>
        );
    }
  };

  const isWideModal = node.data.type === 'agent' || node.data.type === 'workflow';

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className={`bg-gray-800/95 rounded border border-gray-600 shadow-2xl text-white max-h-[80vh] overflow-hidden flex flex-col ${
        isWideModal ? 'w-full max-w-4xl' : 'w-full max-w-md'
      }`}>
        <div className="flex items-center justify-between p-3 border-b border-gray-700/60">
          <h3 className="text-sm font-semibold">Configure {node.data.type} Node</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-0.5">
            <X className="w-4 h-4" />
          </button>
        </div>
        
        <div className="p-3 space-y-3 overflow-y-auto flex-1">
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-1">Label</label>
            <input
              type="text"
              className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Enter node label..."
            />
          </div>
          
          {renderTypeSpecificSettings()}
        </div>
        
        <div className="flex justify-between p-3 border-t border-gray-700/60">
          <button
            onClick={onDelete}
            className="px-3 py-1.5 text-xs bg-red-600 hover:bg-red-700 rounded text-white font-medium transition-colors"
          >
            Delete
          </button>
          <div className="space-x-1.5">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs bg-gray-600 hover:bg-gray-700 rounded text-white font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 rounded text-white font-medium transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Define nodeTypes outside component to prevent re-creation
const customNodeTypes = {
  conversationNode: ConversationNode,
};

// Main Canvas Component
interface FlowCanvasProps {
  nodes: Node<ConversationNodeData>[];
  setNodes: React.Dispatch<React.SetStateAction<Node<ConversationNodeData>[]>>;
  edges: Edge[];
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
  onOpenModal: (id: string) => void;
}

const FlowCanvas: React.FC<FlowCanvasProps> = ({ nodes, setNodes, edges, setEdges, onOpenModal }) => {
  const reactFlowInstance = useReactFlow();

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setNodes((nds) => applyNodeChanges(changes, nds));
    },
    [setNodes]
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      setEdges((eds) => applyEdgeChanges(changes, eds));
    },
    [setEdges]
  );

  const onConnect: OnConnect = useCallback(
    (params: Connection) => {
      const edge = {
        ...params,
        style: { strokeWidth: 2 },
        markerEnd: { 
          type: 'arrowclosed' as const, 
          width: 15, 
          height: 15,
          color: '#6b7280'
        }
      };

      // Color code edges from condition nodes
      if (params.sourceHandle === 'true') {
        edge.style = { ...edge.style  };
        edge.markerEnd = { ...edge.markerEnd, color: '#22c55e' };
      } else if (params.sourceHandle === 'false') {
        edge.style = { ...edge.style  };
        edge.markerEnd = { ...edge.markerEnd, color: '#ef4444' };
      } else {
        edge.style = { ...edge.style  };
      }

      setEdges((eds) => addEdge(edge, eds));
    },
    [setEdges]
  );

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const type = event.dataTransfer.getData('application/reactflow');
      if (!type) return;

      const bounds = event.currentTarget.getBoundingClientRect();
      const position = reactFlowInstance.project({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });

      const nodeType = nodeTypes.find(n => n.type === type);
      if (!nodeType) return;

      const newNode: Node<ConversationNodeData> = {
        id: `${type}-${Date.now()}`,
        type: 'conversationNode',
        position,
        data: {
          label: nodeType.label,
          type: nodeType.type,
          icon: nodeType.icon,
          onOpenModal,
          settings: type === 'response' ? { messages: [{ text: '', type: 'text' }] } : {},
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, setNodes, onOpenModal]
  );

  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  return (
    <div className="flex-1 h-screen" onDrop={onDrop} onDragOver={onDragOver}>
      <ReactFlow
        nodes={nodes.map((node) => ({
          ...node,
          data: { ...node.data, onOpenModal },
        }))}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={customNodeTypes}
        fitView
        className="bg-gray-950"
      >
        <Background color="#374151" gap={12} size={0.5} />
        <Controls className="bg-gray-800/90 border-gray-600" />
        <MiniMap
          className="bg-gray-800/90 border border-gray-600"
          nodeColor={(node) => {
            switch (node.data?.type) {
              case 'start': return '#22c55e';
              case 'end': return '#ef4444';
              case 'message': return '#3b82f6';
              case 'response': return '#6366f1';
              case 'workflow': return '#3b82f6';
              case 'agent': return '#8b5cf6';
              case 'condition': return '#eab308';
              case 'api': return '#06b6d4';
              case 'database': return '#f97316';
              case 'validation': return '#10b981';
              case 'document': return '#ec4899';
              default: return '#6b7280';
            }
          }}
          maskColor="rgba(0, 0, 0, 0.6)"
        />
      </ReactFlow>
    </div>
  );
};

// Main Component
const ConversationGraphBuilderForm: React.FC = () => {
  const { templateNodes, templateEdges } = createDefaultTemplate();
  const [nodes, setNodes] = useNodesState<ConversationNodeData>(templateNodes);
  const [edges, setEdges] = useEdgesState(templateEdges);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [showTestBot, setShowTestBot] = useState(false);

  const openModal = (nodeId: string) => {
    setSelectedNodeId(nodeId);
  };

  // Update template nodes with proper onOpenModal handlers
  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        data: { ...node.data, onOpenModal: openModal }
      }))
    );
  }, [setNodes]);

  const closeModal = () => {
    setSelectedNodeId(null);
  };

  const saveNodeData = (label: string, settings: Record<string, any>) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === selectedNodeId
          ? {
              ...node,
              data: {
                ...node.data,
                label,
                settings,
              },
            }
          : node
      )
    );
    setSelectedNodeId(null);
  };

  const deleteNode = () => {
    if (!selectedNodeId) return;
    
    setNodes((nds) => nds.filter((node) => node.id !== selectedNodeId));
    setEdges((eds) => eds.filter((edge) => 
      edge.source !== selectedNodeId && edge.target !== selectedNodeId
    ));
    setSelectedNodeId(null);
  };

  const saveGraph = () => {
    const graphData = { nodes, edges };
    const blob = new Blob([JSON.stringify(graphData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'conversation-graph.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearGraph = () => {
    setNodes([]);
    setEdges([]);
  };

  const loadTemplate = () => {
    const { templateNodes, templateEdges } = createDefaultTemplate();
    setNodes(templateNodes.map(node => ({
      ...node,
      data: { ...node.data, onOpenModal: openModal }
    })));
    setEdges(templateEdges);
  };

  const selectedNode = nodes.find((n) => n.id === selectedNodeId);

  return (
    <div className="portal-content flex h-screen bg-gray-950 text-white">
      <ConversationSidebar />
      
      <div className="flex-1 flex flex-col">
        <div className="bg-gray-900/95 border-b border-gray-700/60 px-4 py-2 flex justify-between items-center">
          <h1 className="text-sm font-semibold">Conversation Graph Builder</h1>
          <div className="flex gap-2">
            <button
              onClick={loadTemplate}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 rounded font-medium transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              Template
            </button>
            <button
              onClick={clearGraph}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gray-600 hover:bg-gray-700 rounded font-medium transition-colors"
            >
              <Trash2 className="w-3 h-3" />
              Clear
            </button>
            <button
              onClick={() => setShowTestBot(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-purple-600 hover:bg-purple-700 rounded font-medium transition-colors"
            >
              <TestTube className="w-3 h-3" />
              Test
            </button>
            <button
              onClick={saveGraph}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-green-600 hover:bg-green-700 rounded font-medium transition-colors"
            >
              <Download className="w-3 h-3" />
              Export
            </button>
          </div>
        </div>
        
        <ReactFlowProvider>
          <FlowCanvas
            nodes={nodes}
            setNodes={setNodes}
            edges={edges}
            setEdges={setEdges}
            onOpenModal={openModal}
          />
        </ReactFlowProvider>
      </div>

      {selectedNodeId && (
        <ConfigModal
          node={selectedNode || null}
          onSave={saveNodeData}
          onDelete={deleteNode}
          onClose={closeModal}
        />
      )}

      {showTestBot && (
        <TestChatbot onClose={() => setShowTestBot(false)} />
      )}
    </div>
  );
};

export default ConversationGraphBuilderForm;

// import React, { useCallback, useState } from 'react';
// import ReactFlow, {
//   addEdge,
//   MiniMap,
//   Background,
//   Controls,
//   useNodesState,
//   useEdgesState,
//   ReactFlowProvider,
//   useReactFlow,
//   Node,
//   Edge,
//   OnConnect,
//   Connection,
//   NodeChange,
//   EdgeChange,
//   applyNodeChanges,
//   applyEdgeChanges,
//   Handle,
//   Position,
//   NodeProps,
// } from 'reactflow';
// import 'reactflow/dist/style.css';
// import { Play, Square, GitBranch, Bot, Workflow, X, Save, Download, MessageSquare, MessageCircle, Plus, Database, Globe, Shield, FileText, TestTube, Send } from 'lucide-react';

// // Node data interface
// interface ConversationNodeData {
//   label: string;
//   type: string;
//   icon: React.ReactNode;
//   onOpenModal: (id: string) => void;
//   settings?: Record<string, any>;
// }

// // Mock data for agents and workflows
// const mockAgents = [
//   {
//     id: 'bank-statement',
//     name: 'Bank Statement Verification',
//     description: 'Verifies user identity for bank statement access',
//     parameters: {
//       required: ['$role', '$tenantId', 'dob', 'pin', 'idNumber'],
//       optional: ['$uid', '$sessionId', 'accountType'],
//       outputs: ['verification_status', 'access_token', 'statement_url']
//     },
//     userMessages: {
//       dob: 'Please share your date of birth (DD/MM/YYYY)',
//       pin: 'Please enter your 4-digit PIN',
//       idNumber: 'Please provide your government ID number'
//     }
//   },
//   {
//     id: 'loan-application',
//     name: 'Loan Application Agent',
//     description: 'Handles loan application process and validation',
//     parameters: {
//       required: ['$role', '$tenantId', 'income', 'employment', 'loanAmount'],
//       optional: ['$uid', 'creditScore', 'collateral'],
//       outputs: ['application_id', 'eligibility_status', 'next_steps']
//     },
//     userMessages: {
//       income: 'What is your monthly income?',
//       employment: 'Please provide your employment details',
//       loanAmount: 'How much loan amount do you need?'
//     }
//   },
//   {
//     id: 'customer-support',
//     name: 'Customer Support Agent',
//     description: 'Handles customer inquiries and support tickets',
//     parameters: {
//       required: ['$role', '$tenantId', 'issueType', 'priority'],
//       optional: ['$uid', '$sessionId', 'previousTickets'],
//       outputs: ['ticket_id', 'resolution_status', 'escalation_needed']
//     },
//     userMessages: {
//       issueType: 'Please describe your issue',
//       priority: 'How urgent is this issue? (Low/Medium/High)'
//     }
//   }
// ];

// const mockWorkflows = [
//   {
//     id: 'kyc-verification',
//     name: 'KYC Verification Flow',
//     description: 'Complete Know Your Customer verification process',
//     parameters: {
//       required: ['$tenantId', 'documentType', 'documentNumber'],
//       optional: ['$uid', 'nationality', 'address'],
//       outputs: ['kyc_status', 'verification_id', 'compliance_score']
//     },
//     userMessages: {
//       documentType: 'What type of document do you want to verify? (Passport/License/ID)',
//       documentNumber: 'Please enter your document number'
//     }
//   },
//   {
//     id: 'payment-processing',
//     name: 'Payment Processing',
//     description: 'Handle payment transactions and billing',
//     parameters: {
//       required: ['$tenantId', 'amount', 'currency'],
//       optional: ['$uid', 'paymentMethod', 'billingAddress'],
//       outputs: ['transaction_id', 'payment_status', 'receipt_url']
//     },
//     userMessages: {
//       amount: 'Enter the payment amount',
//       currency: 'Select currency (USD/EUR/GBP)'
//     }
//   }
// ];

// // Node types for the sidebar
// const nodeTypes = [
//   { type: 'start', icon: <Play className="w-3 h-3 text-green-400" />, label: 'Start' },
//   { type: 'message', icon: <MessageSquare className="w-3 h-3 text-blue-400" />, label: 'Message' },
//   { type: 'response', icon: <MessageCircle className="w-3 h-3 text-indigo-400" />, label: 'Response' },
//   { type: 'agent', icon: <Bot className="w-3 h-3 text-purple-400" />, label: 'Agent' },
//   { type: 'workflow', icon: <Workflow className="w-3 h-3 text-blue-400" />, label: 'Workflow' },
//   { type: 'condition', icon: <GitBranch className="w-3 h-3 text-yellow-400" />, label: 'Condition' },
//   { type: 'api', icon: <Globe className="w-3 h-3 text-cyan-400" />, label: 'API Call' },
//   { type: 'database', icon: <Database className="w-3 h-3 text-orange-400" />, label: 'Database' },
//   { type: 'validation', icon: <Shield className="w-3 h-3 text-emerald-400" />, label: 'Validation' },
//   { type: 'document', icon: <FileText className="w-3 h-3 text-pink-400" />, label: 'Document' },
//   { type: 'end', icon: <Square className="w-3 h-3 text-red-400" />, label: 'End' },
// ];

// // Custom Node Component
// const ConversationNode: React.FC<NodeProps<ConversationNodeData>> = ({ id, data, selected }) => {
//   const getNodeColor = (type: string) => {
//     switch (type) {
//       case 'start': return 'border-green-400 bg-green-950/40';
//       case 'end': return 'border-red-400 bg-red-950/40';
//       case 'message': return 'border-blue-400 bg-blue-950/40';
//       case 'response': return 'border-indigo-400 bg-indigo-950/40';
//       case 'workflow': return 'border-blue-400 bg-blue-950/40';
//       case 'agent': return 'border-purple-400 bg-purple-950/40';
//       case 'condition': return 'border-yellow-400 bg-yellow-950/40';
//       case 'api': return 'border-cyan-400 bg-cyan-950/40';
//       case 'database': return 'border-orange-400 bg-orange-950/40';
//       case 'validation': return 'border-emerald-400 bg-emerald-950/40';
//       case 'document': return 'border-pink-400 bg-pink-950/40';
//       default: return 'border-gray-400 bg-gray-950/40';
//     }
//   };

//   const renderHandles = () => {
//     if (data.type === 'condition') {
//       return (
//         <>
//           <Handle
//             type="target"
//             position={Position.Left}
//             className="w-2 h-2 bg-gray-500 border border-white/60"
//           />
//           <Handle
//             type="source"
//             position={Position.Right}
//             id="true"
//             style={{ top: '30%' }}
//             className="w-2 h-2 bg-green-500 border border-white/60"
//           />
//           <Handle
//             type="source"
//             position={Position.Right}
//             id="false"
//             style={{ top: '70%' }}
//             className="w-2 h-2 bg-red-500 border border-white/60"
//           />
//           {/* True/False labels */}
//           <div className="absolute -right-8 top-2 text-xs text-green-400 font-medium">True</div>
//           <div className="absolute -right-8 bottom-2 text-xs text-red-400 font-medium">False</div>
//         </>
//       );
//     }

//     return (
//       <>
//         {data.type !== 'start' && (
//           <Handle
//             type="target"
//             position={Position.Left}
//             className="w-2 h-2 bg-gray-500 border border-white/60"
//           />
//         )}
//         {data.type !== 'end' && (
//           <Handle
//             type="source"
//             position={Position.Right}
//             className="w-2 h-2 bg-gray-500 border border-white/60"
//           />
//         )}
//       </>
//     );
//   };

//   return (
//     <div
//       className={`px-2 py-1.5 shadow-lg rounded border bg-gray-900/95 text-white min-w-20 cursor-pointer transition-all hover:shadow-xl relative ${getNodeColor(data.type)} ${
//         selected ? 'ring-1 ring-white/60 shadow-white/20' : ''
//       }`}
//       onClick={(e) => {
//         e.stopPropagation();
//         data.onOpenModal(id);
//       }}
//     >
//       {renderHandles()}
      
//       <div className="flex flex-col items-center gap-0.5">
//         {data.icon}
//         <div className="text-xs font-medium text-center leading-tight max-w-16 truncate">
//           {data.label}
//         </div>
//         {data.settings?.intentLabel && (
//           <div className="text-xs text-gray-400 truncate max-w-16">
//             {data.settings.intentLabel}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// // Sidebar Component
// const ConversationSidebar: React.FC = () => {
//   const onDragStart = (event: React.DragEvent<HTMLDivElement>, nodeType: string) => {
//     event.dataTransfer.setData('application/reactflow', nodeType);
//     event.dataTransfer.effectAllowed = 'move';
//   };

//   return (
//     <div className="w-52 bg-gray-900/95 border-r border-gray-700/60 p-3">
//       <h3 className="text-sm font-semibold text-white mb-3">Components</h3>
//       <div className="space-y-1.5">
//         {nodeTypes.map((node) => (
//           <div
//             key={node.type}
//             className="flex items-center gap-2 p-2 bg-gray-800/60 rounded border border-gray-600/40 hover:bg-gray-700/60 cursor-grab transition-colors text-xs"
//             onDragStart={(event) => onDragStart(event, node.type)}
//             draggable
//           >
//             {node.icon}
//             <span className="font-medium text-white">{node.label}</span>
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// };

// // Test Chatbot Component
// interface TestChatbotProps {
//   onClose: () => void;
// }

// const TestChatbot: React.FC<TestChatbotProps> = ({ onClose }) => {
//   const [messages, setMessages] = useState([
//     { text: 'Hello! I\'m your test bot. How can I help you today?', sender: 'bot' }
//   ]);
//   const [input, setInput] = useState('');

//   const sendMessage = () => {
//     if (!input.trim()) return;
    
//     const newMessages = [
//       ...messages,
//       { text: input, sender: 'user' },
//       { text: 'This is a test response. In a real implementation, this would process through your conversation graph.', sender: 'bot' }
//     ];
    
//     setMessages(newMessages);
//     setInput('');
//   };

//   return (
//     <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
//       <div className="bg-gray-800 rounded-lg border border-gray-600 shadow-2xl w-full max-w-md h-96 flex flex-col">
//         <div className="flex items-center justify-between p-3 border-b border-gray-700">
//           <h3 className="text-sm font-semibold text-white">Test Chatbot</h3>
//           <button onClick={onClose} className="text-gray-400 hover:text-white">
//             <X className="w-4 h-4" />
//           </button>
//         </div>
        
//         <div className="flex-1 overflow-y-auto p-3 space-y-2">
//           {messages.map((message, index) => (
//             <div
//               key={index}
//               className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
//             >
//               <div
//                 className={`max-w-xs px-3 py-2 rounded-lg text-xs ${
//                   message.sender === 'user'
//                     ? 'bg-blue-600 text-white'
//                     : 'bg-gray-700 text-gray-200'
//                 }`}
//               >
//                 {message.text}
//               </div>
//             </div>
//           ))}
//         </div>
        
//         <div className="p-3 border-t border-gray-700">
//           <div className="flex gap-2">
//             <input
//               type="text"
//               value={input}
//               onChange={(e) => setInput(e.target.value)}
//               onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
//               placeholder="Type your message..."
//               className="flex-1 px-3 py-2 text-xs bg-gray-700 border border-gray-600 rounded text-white"
//             />
//             <button
//               onClick={sendMessage}
//               className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded transition-colors"
//             >
//               <Send className="w-3 h-3 text-white" />
//             </button>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// // Configuration Modal Component
// interface ConfigModalProps {
//   node: Node<ConversationNodeData> | null;
//   onSave: (label: string, settings: Record<string, any>) => void;
//   onDelete: () => void;
//   onClose: () => void;
// }

// const ConfigModal: React.FC<ConfigModalProps> = ({ node, onSave, onDelete, onClose }) => {
//   const [label, setLabel] = useState(node?.data.label || '');
//   const [settings, setSettings] = useState<Record<string, any>>(node?.data.settings || {});

//   if (!node) return null;

//   const handleSave = () => {
//     onSave(label, settings);
//   };

//   const addResponseMessage = () => {
//     const messages = settings.messages || [];
//     setSettings({
//       ...settings,
//       messages: [...messages, { text: '', type: 'text' }]
//     });
//   };

//   const updateResponseMessage = (index: number, text: string) => {
//     const messages = [...(settings.messages || [])];
//     messages[index] = { ...messages[index], text };
//     setSettings({ ...settings, messages });
//   };

//   const removeResponseMessage = (index: number) => {
//     const messages = [...(settings.messages || [])];
//     messages.splice(index, 1);
//     setSettings({ ...settings, messages });
//   };

//   const updateParameterMessage = (paramName: string, message: string) => {
//     const parameterMessages = { ...settings.parameterMessages };
//     parameterMessages[paramName] = message;
//     setSettings({ ...settings, parameterMessages });
//   };

//   const renderParameterMapping = (agent: any) => {
//     const userParams = agent.parameters.required.filter((p: string) => !p.startsWith('$'));
    
//     return (
//       <div className="border-l border-gray-600 pl-3 ml-3">
//         <h4 className="text-xs font-medium text-gray-300 mb-2">User Message Mapping</h4>
//         <div className="space-y-2">
//           {userParams.map((param: string) => (
//             <div key={param}>
//               <label className="block text-xs text-gray-400 mb-1 capitalize">
//                 {param.replace(/([A-Z])/g, ' $1').trim()}
//               </label>
//               <textarea
//                 className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white resize-none"
//                 rows={2}
//                 value={settings.parameterMessages?.[param] || agent.userMessages?.[param] || ''}
//                 onChange={(e) => updateParameterMessage(param, e.target.value)}
//                 placeholder={`Message to ask user for ${param}...`}
//               />
//               <div className="text-xs text-gray-500 mt-1">
//                 Users can provide multiple values separated by comma (,) or pipe (|)
//               </div>
//             </div>
//           ))}
//         </div>
//       </div>
//     );
//   };

//   const renderTypeSpecificSettings = () => {
//     switch (node.data.type) {
//       case 'message':
//         return (
//           <div className="space-y-3">
//             <div>
//               <label className="block text-xs font-medium text-gray-300 mb-1">Expected Intent</label>
//               <input
//                 type="text"
//                 className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
//                 value={settings.expectedIntent || ''}
//                 onChange={(e) => setSettings({ ...settings, expectedIntent: e.target.value })}
//                 placeholder="e.g., greeting, support_request, complaint"
//               />
//             </div>
//             <div>
//               <label className="block text-xs font-medium text-gray-300 mb-1">Intent Label</label>
//               <input
//                 type="text"
//                 className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
//                 value={settings.intentLabel || ''}
//                 onChange={(e) => setSettings({ ...settings, intentLabel: e.target.value })}
//                 placeholder="Label to show on node"
//               />
//             </div>
//             <div>
//               <label className="block text-xs font-medium text-gray-300 mb-1">Expected Values</label>
//               <textarea
//                 className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white resize-none"
//                 rows={3}
//                 value={settings.expectedValues || ''}
//                 onChange={(e) => setSettings({ ...settings, expectedValues: e.target.value })}
//                 placeholder="user_name, email, issue_type..."
//               />
//             </div>
//           </div>
//         );

//       case 'response':
//         return (
//           <div className="space-y-3">
//             <div className="flex items-center justify-between">
//               <label className="block text-xs font-medium text-gray-300">Response Messages</label>
//               <button
//                 onClick={addResponseMessage}
//                 className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 rounded transition-colors"
//               >
//                 <Plus className="w-3 h-3" />
//                 Add
//               </button>
//             </div>
//             <div className="space-y-2 max-h-40 overflow-y-auto">
//               {(settings.messages || []).map((message: any, index: number) => (
//                 <div key={index} className="flex gap-2">
//                   <textarea
//                     className="flex-1 p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white resize-none"
//                     rows={2}
//                     value={message.text}
//                     onChange={(e) => updateResponseMessage(index, e.target.value)}
//                     placeholder={`Message ${index + 1}...`}
//                   />
//                   <button
//                     onClick={() => removeResponseMessage(index)}
//                     className="text-red-400 hover:text-red-300 p-1"
//                   >
//                     <X className="w-3 h-3" />
//                   </button>
//                 </div>
//               ))}
//             </div>
//           </div>
//         );

//       case 'agent':
//         const selectedAgent = mockAgents.find(a => a.id === settings.selectedAgent);
//         return (
//           <div className="grid grid-cols-2 gap-4">
//             <div className="space-y-3">
//               <div>
//                 <label className="block text-xs font-medium text-gray-300 mb-1">Select Agent</label>
//                 <select
//                   className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
//                   value={settings.selectedAgent || ''}
//                   onChange={(e) => {
//                     const agent = mockAgents.find(a => a.id === e.target.value);
//                     setSettings({ 
//                       ...settings, 
//                       selectedAgent: e.target.value,
//                       agentConfig: agent ? agent.parameters : {},
//                       parameterMessages: agent ? { ...agent.userMessages } : {}
//                     });
//                   }}
//                 >
//                   <option value="">Choose an agent...</option>
//                   {mockAgents.map((agent) => (
//                     <option key={agent.id} value={agent.id}>{agent.name}</option>
//                   ))}
//                 </select>
//               </div>
              
//               {selectedAgent && (
//                 <div>
//                   <div className="text-xs text-gray-400 mb-2">
//                     {selectedAgent.description}
//                   </div>
//                   <label className="block text-xs font-medium text-gray-300 mb-1">Parameters</label>
//                   <div className="bg-gray-700 p-2 rounded text-xs">
//                     <pre className="text-gray-300 whitespace-pre-wrap">
//                       {JSON.stringify(selectedAgent.parameters, null, 2)}
//                     </pre>
//                   </div>
//                 </div>
//               )}
//             </div>
            
//             {selectedAgent && renderParameterMapping(selectedAgent)}
//           </div>
//         );

//       case 'workflow':
//         const selectedWorkflow = mockWorkflows.find(w => w.id === settings.selectedWorkflow);
//         return (
//           <div className="grid grid-cols-2 gap-4">
//             <div className="space-y-3">
//               <div>
//                 <label className="block text-xs font-medium text-gray-300 mb-1">Select Workflow</label>
//                 <select
//                   className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
//                   value={settings.selectedWorkflow || ''}
//                   onChange={(e) => {
//                     const workflow = mockWorkflows.find(w => w.id === e.target.value);
//                     setSettings({ 
//                       ...settings, 
//                       selectedWorkflow: e.target.value,
//                       workflowConfig: workflow ? workflow.parameters : {},
//                       parameterMessages: workflow ? { ...workflow.userMessages } : {}
//                     });
//                   }}
//                 >
//                   <option value="">Choose a workflow...</option>
//                   {mockWorkflows.map((workflow) => (
//                     <option key={workflow.id} value={workflow.id}>{workflow.name}</option>
//                   ))}
//                 </select>
//               </div>
              
//               {selectedWorkflow && (
//                 <div>
//                   <div className="text-xs text-gray-400 mb-2">
//                     {selectedWorkflow.description}
//                   </div>
//                   <label className="block text-xs font-medium text-gray-300 mb-1">Parameters</label>
//                   <div className="bg-gray-700 p-2 rounded text-xs">
//                     <pre className="text-gray-300 whitespace-pre-wrap">
//                       {JSON.stringify(selectedWorkflow.parameters, null, 2)}
//                     </pre>
//                   </div>
//                 </div>
//               )}
//             </div>
            
//             {selectedWorkflow && renderParameterMapping(selectedWorkflow)}
//           </div>
//         );

//       case 'condition':
//         return (
//           <div className="space-y-3">
//             <div>
//               <label className="block text-xs font-medium text-gray-300 mb-1">Condition</label>
//               <input
//                 type="text"
//                 className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
//                 value={settings.condition || ''}
//                 onChange={(e) => setSettings({ ...settings, condition: e.target.value })}
//                 placeholder="e.g., user_intent == 'support'"
//               />
//             </div>
//             <div className="grid grid-cols-2 gap-2">
//               <div>
//                 <label className="block text-xs font-medium text-green-400 mb-1">True Branch</label>
//                 <input
//                   type="text"
//                   className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
//                   value={settings.trueBranch || 'Yes'}
//                   onChange={(e) => setSettings({ ...settings, trueBranch: e.target.value })}
//                 />
//               </div>
//               <div>
//                 <label className="block text-xs font-medium text-red-400 mb-1">False Branch</label>
//                 <input
//                   type="text"
//                   className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
//                   value={settings.falseBranch || 'No'}
//                   onChange={(e) => setSettings({ ...settings, falseBranch: e.target.value })}
//                 />
//               </div>
//             </div>
//           </div>
//         );

//       case 'api':
//         return (
//           <div className="space-y-3">
//             <div>
//               <label className="block text-xs font-medium text-gray-300 mb-1">API Endpoint</label>
//               <input
//                 type="text"
//                 className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
//                 value={settings.endpoint || ''}
//                 onChange={(e) => setSettings({ ...settings, endpoint: e.target.value })}
//                 placeholder="https://api.example.com/endpoint"
//               />
//             </div>
//             <div className="grid grid-cols-2 gap-2">
//               <div>
//                 <label className="block text-xs font-medium text-gray-300 mb-1">Method</label>
//                 <select
//                   className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
//                   value={settings.method || 'GET'}
//                   onChange={(e) => setSettings({ ...settings, method: e.target.value })}
//                 >
//                   <option value="GET">GET</option>
//                   <option value="POST">POST</option>
//                   <option value="PUT">PUT</option>
//                   <option value="DELETE">DELETE</option>
//                 </select>
//               </div>
//               <div>
//                 <label className="block text-xs font-medium text-gray-300 mb-1">Timeout (ms)</label>
//                 <input
//                   type="number"
//                   className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
//                   value={settings.timeout || 5000}
//                   onChange={(e) => setSettings({ ...settings, timeout: parseInt(e.target.value) })}
//                 />
//               </div>
//             </div>
//           </div>
//         );

//       case 'database':
//         return (
//           <div className="space-y-3">
//             <div>
//               <label className="block text-xs font-medium text-gray-300 mb-1">Query Type</label>
//               <select
//                 className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
//                 value={settings.queryType || ''}
//                 onChange={(e) => setSettings({ ...settings, queryType: e.target.value })}
//               >
//                 <option value="">Select query type...</option>
//                 <option value="SELECT">SELECT</option>
//                 <option value="INSERT">INSERT</option>
//                 <option value="UPDATE">UPDATE</option>
//                 <option value="DELETE">DELETE</option>
//               </select>
//             </div>
//             <div>
//               <label className="block text-xs font-medium text-gray-300 mb-1">Table Name</label>
//               <input
//                 type="text"
//                 className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
//                 value={settings.tableName || ''}
//                 onChange={(e) => setSettings({ ...settings, tableName: e.target.value })}
//                 placeholder="users, transactions, etc."
//               />
//             </div>
//           </div>
//         );

//       case 'validation':
//         return (
//           <div className="space-y-3">
//             <div>
//               <label className="block text-xs font-medium text-gray-300 mb-1">Validation Type</label>
//               <select
//                 className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
//                 value={settings.validationType || ''}
//                 onChange={(e) => setSettings({ ...settings, validationType: e.target.value })}
//               >
//                 <option value="">Select validation...</option>
//                 <option value="email">Email Format</option>
//                 <option value="phone">Phone Number</option>
//                 <option value="date">Date Format</option>
//                 <option value="custom">Custom Regex</option>
//               </select>
//             </div>
//             {settings.validationType === 'custom' && (
//               <div>
//                 <label className="block text-xs font-medium text-gray-300 mb-1">Regex Pattern</label>
//                 <input
//                   type="text"
//                   className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
//                   value={settings.regex || ''}
//                   onChange={(e) => setSettings({ ...settings, regex: e.target.value })}
//                   placeholder="^[A-Za-z0-9]+$"
//                 />
//               </div>
//             )}
//           </div>
//         );

//       default:
//         return (
//           <div>
//             <label className="block text-xs font-medium text-gray-300 mb-1">Description</label>
//             <textarea
//               className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white resize-none"
//               rows={3}
//               value={settings.description || ''}
//               onChange={(e) => setSettings({ ...settings, description: e.target.value })}
//               placeholder="Enter description..."
//             />
//           </div>
//         );
//     }
//   };

//   const isWideModal = node.data.type === 'agent' || node.data.type === 'workflow';

//   return (
//     <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
//       <div className={`bg-gray-800/95 rounded border border-gray-600 shadow-2xl text-white max-h-[80vh] overflow-hidden flex flex-col ${
//         isWideModal ? 'w-full max-w-4xl' : 'w-full max-w-md'
//       }`}>
//         <div className="flex items-center justify-between p-3 border-b border-gray-700/60">
//           <h3 className="text-sm font-semibold">Configure {node.data.type} Node</h3>
//           <button onClick={onClose} className="text-gray-400 hover:text-white p-0.5">
//             <X className="w-4 h-4" />
//           </button>
//         </div>
        
//         <div className="p-3 space-y-3 overflow-y-auto flex-1">
//           <div>
//             <label className="block text-xs font-medium text-gray-300 mb-1">Label</label>
//             <input
//               type="text"
//               className="w-full p-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
//               value={label}
//               onChange={(e) => setLabel(e.target.value)}
//               placeholder="Enter node label..."
//             />
//           </div>
          
//           {renderTypeSpecificSettings()}
//         </div>
        
//         <div className="flex justify-between p-3 border-t border-gray-700/60">
//           <button
//             onClick={onDelete}
//             className="px-3 py-1.5 text-xs bg-red-600 hover:bg-red-700 rounded text-white font-medium transition-colors"
//           >
//             Delete
//           </button>
//           <div className="space-x-1.5">
//             <button
//               onClick={onClose}
//               className="px-3 py-1.5 text-xs bg-gray-600 hover:bg-gray-700 rounded text-white font-medium transition-colors"
//             >
//               Cancel
//             </button>
//             <button
//               onClick={handleSave}
//               className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 rounded text-white font-medium transition-colors"
//             >
//               Save
//             </button>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// // Define nodeTypes outside component to prevent re-creation
// const customNodeTypes = {
//   conversationNode: ConversationNode,
// };

// // Main Canvas Component
// interface FlowCanvasProps {
//   nodes: Node<ConversationNodeData>[];
//   setNodes: React.Dispatch<React.SetStateAction<Node<ConversationNodeData>[]>>;
//   edges: Edge[];
//   setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
//   onOpenModal: (id: string) => void;
// }

// const FlowCanvas: React.FC<FlowCanvasProps> = ({ nodes, setNodes, edges, setEdges, onOpenModal }) => {
//   const reactFlowInstance = useReactFlow();

//   const onNodesChange = useCallback(
//     (changes: NodeChange[]) => {
//       setNodes((nds) => applyNodeChanges(changes, nds));
//     },
//     [setNodes]
//   );

//   const onEdgesChange = useCallback(
//     (changes: EdgeChange[]) => {
//       setEdges((eds) => applyEdgeChanges(changes, eds));
//     },
//     [setEdges]
//   );

//   const onConnect: OnConnect = useCallback(
//     (params: Connection) => {
//       const edge = {
//         ...params,
//         style: { strokeWidth: 2 },
//         markerEnd: { 
//           type: 'arrowclosed' as const, 
//           width: 15, 
//           height: 15,
//           color: '#6b7280'
//         }
//       };

//       // Color code edges from condition nodes
//       if (params.sourceHandle === 'true') {
//         edge.style = { ...edge.style };
//         edge.markerEnd = { ...edge.markerEnd, color: '#22c55e' };
//       } else if (params.sourceHandle === 'false') {
//         edge.style = { ...edge.style };
//         edge.markerEnd = { ...edge.markerEnd, color: '#ef4444' };
//       } else {
//         edge.style = { ...edge.style };
//       }

//       setEdges((eds) => addEdge(edge, eds));
//     },
//     [setEdges]
//   );

//   const onDrop = useCallback(
//     (event: React.DragEvent<HTMLDivElement>) => {
//       event.preventDefault();
//       const type = event.dataTransfer.getData('application/reactflow');
//       if (!type) return;

//       const bounds = event.currentTarget.getBoundingClientRect();
//       const position = reactFlowInstance.project({
//         x: event.clientX - bounds.left,
//         y: event.clientY - bounds.top,
//       });

//       const nodeType = nodeTypes.find(n => n.type === type);
//       if (!nodeType) return;

//       const newNode: Node<ConversationNodeData> = {
//         id: `${type}-${Date.now()}`,
//         type: 'conversationNode',
//         position,
//         data: {
//           label: nodeType.label,
//           type: nodeType.type,
//           icon: nodeType.icon,
//           onOpenModal,
//           settings: type === 'response' ? { messages: [{ text: '', type: 'text' }] } : {},
//         },
//       };

//       setNodes((nds) => nds.concat(newNode));
//     },
//     [reactFlowInstance, setNodes, onOpenModal]
//   );

//   const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
//     event.preventDefault();
//     event.dataTransfer.dropEffect = 'move';
//   };

//   return (
//     <div className="flex-1 h-screen" onDrop={onDrop} onDragOver={onDragOver}>
//       <ReactFlow
//         nodes={nodes.map((node) => ({
//           ...node,
//           data: { ...node.data, onOpenModal },
//         }))}
//         edges={edges}
//         onNodesChange={onNodesChange}
//         onEdgesChange={onEdgesChange}
//         onConnect={onConnect}
//         nodeTypes={customNodeTypes}
//         fitView
//         className="bg-gray-950"
//       >
//         <Background color="#374151" gap={12} size={0.5} />
//         <Controls className="bg-gray-800/90 border-gray-600" />
//         <MiniMap
//           className="bg-gray-800/90 border border-gray-600"
//           nodeColor={(node) => {
//             switch (node.data?.type) {
//               case 'start': return '#22c55e';
//               case 'end': return '#ef4444';
//               case 'message': return '#3b82f6';
//               case 'response': return '#6366f1';
//               case 'workflow': return '#3b82f6';
//               case 'agent': return '#8b5cf6';
//               case 'condition': return '#eab308';
//               case 'api': return '#06b6d4';
//               case 'database': return '#f97316';
//               case 'validation': return '#10b981';
//               case 'document': return '#ec4899';
//               default: return '#6b7280';
//             }
//           }}
//           maskColor="rgba(0, 0, 0, 0.6)"
//         />
//       </ReactFlow>
//     </div>
//   );
// };

// // Main Component
// const ConversationGraphBuilderForm: React.FC = () => {
//   const [nodes, setNodes] = useNodesState<ConversationNodeData>([]);
//   const [edges, setEdges] = useEdgesState([]);
//   const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
//   const [showTestBot, setShowTestBot] = useState(false);

//   const openModal = (nodeId: string) => {
//     setSelectedNodeId(nodeId);
//   };

//   const closeModal = () => {
//     setSelectedNodeId(null);
//   };

//   const saveNodeData = (label: string, settings: Record<string, any>) => {
//     setNodes((nds) =>
//       nds.map((node) =>
//         node.id === selectedNodeId
//           ? {
//               ...node,
//               data: {
//                 ...node.data,
//                 label,
//                 settings,
//               },
//             }
//           : node
//       )
//     );
//     setSelectedNodeId(null);
//   };

//   const deleteNode = () => {
//     if (!selectedNodeId) return;
    
//     setNodes((nds) => nds.filter((node) => node.id !== selectedNodeId));
//     setEdges((eds) => eds.filter((edge) => 
//       edge.source !== selectedNodeId && edge.target !== selectedNodeId
//     ));
//     setSelectedNodeId(null);
//   };

//   const saveGraph = () => {
//     const graphData = { nodes, edges };
//     const blob = new Blob([JSON.stringify(graphData, null, 2)], { type: 'application/json' });
//     const url = URL.createObjectURL(blob);
//     const a = document.createElement('a');
//     a.href = url;
//     a.download = 'conversation-graph.json';
//     a.click();
//     URL.revokeObjectURL(url);
//   };

//   const selectedNode = nodes.find((n) => n.id === selectedNodeId);

//   return (
//     <div className="flex h-screen bg-gray-950 text-white">
//       <ConversationSidebar />
      
//       <div className="flex-1 flex flex-col">
//         <div className="bg-gray-900/95 border-b border-gray-700/60 px-4 py-2 flex justify-between items-center">
//           <h1 className="text-sm font-semibold">Conversation Graph Builder</h1>
//           <div className="flex gap-2">
//             <button
//               onClick={() => setShowTestBot(true)}
//               className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-purple-600 hover:bg-purple-700 rounded font-medium transition-colors"
//             >
//               <TestTube className="w-3 h-3" />
//               Test
//             </button>
//             <button
//               onClick={saveGraph}
//               className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-green-600 hover:bg-green-700 rounded font-medium transition-colors"
//             >
//               <Download className="w-3 h-3" />
//               Export
//             </button>
//           </div>
//         </div>
        
//         <ReactFlowProvider>
//           <FlowCanvas
//             nodes={nodes}
//             setNodes={setNodes}
//             edges={edges}
//             setEdges={setEdges}
//             onOpenModal={openModal}
//           />
//         </ReactFlowProvider>
//       </div>

//       {selectedNodeId && (
//         <ConfigModal
//           node={selectedNode || null}
//           onSave={saveNodeData}
//           onDelete={deleteNode}
//           onClose={closeModal}
//         />
//       )}

//       {showTestBot && (
//         <TestChatbot onClose={() => setShowTestBot(false)} />
//       )}
//     </div>
//   );
// };

// export default ConversationGraphBuilderForm;
 